import React, { useEffect, useRef, useState } from "react";
import { ForestIcon, type ForestIconName, api } from "./app-core";
import { formatNumber, t } from "./i18n";
import type { PublicStats, StatKey } from "../../src/public-stats-contract";

// D-123: the home page's "real numbers" strip. The Worker publishes only totals that passed its floors
// (src/public-stats.ts); while loading, on any failure, or with too few numbers, nothing is rendered.
const ORDER: StatKey[] = ["classes", "students", "tools", "scores"];
const ICONS: Record<StatKey, ForestIconName> = { classes: "classes", students: "students", tools: "blackboard", scores: "star" };
const COUNT_UP_MS = 1400;

const captions = (): Record<StatKey, string> => ({
  classes: t("個班級在使用"),
  students: t("位學生在使用"),
  tools: t("次"),
  scores: t("次"),
});
const introductions = (): Record<StatKey, string> => ({
  classes: t("已有"),
  students: t("已有"),
  tools: t("教師工具已使用"),
  scores: t("已完成記分"),
});

const prefersReducedMotion = () => {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return true; }
};

/** Counts from 0 to `value` once `play` turns true; the readable total is always in the DOM for assistive tech. */
function CountUp({ value, play }: { value: number; play: boolean }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!play) return;
    if (prefersReducedMotion()) { setShown(value); return; }
    let frame = 0;
    const started = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - started) / COUNT_UP_MS);
      setShown(Math.round(value * (1 - Math.pow(1 - progress, 3))));
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [play, value]);
  return <><span className="home-stat-number" aria-hidden="true">{formatNumber(play ? shown : 0)}</span><span className="sr-only">{formatNumber(value)}</span></>;
}

/** Hand-drawn climbing vine, a quiet corner decoration (aria-hidden). */
const Vine = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 120 160" aria-hidden="true"><path d="M8 0c30 30 6 52 40 78s14 52 44 82" /><path d="M30 34c14-2 22-12 22-24-14 0-22 10-22 24Z" /><path d="M44 74c14 0 24-10 24-22-14 0-24 10-24 22Z" /><path d="M24 62c-12-2-18-12-16-22 12 2 18 10 16 22Z" /><path d="M64 112c14 0 24-8 26-20-14-2-24 6-26 20Z" /><path d="M54 132c-10-2-16-10-14-20 10 2 16 8 14 20Z" /></svg>
);

export function HomeStats() {
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [play, setPlay] = useState(false);
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api<PublicStats>("/api/public-stats").then((data) => { if (!cancelled) setStats(data); }).catch(() => { /* no numbers is better than wrong ones */ });
    // After the first paint work: the strip never competes with the hero image.
    const idleCallback = (window as Window & { requestIdleCallback?: (fn: () => void, options?: { timeout: number }) => number }).requestIdleCallback;
    const handle = idleCallback ? idleCallback(load, { timeout: 2500 }) : window.setTimeout(load, 800);
    return () => { cancelled = true; if (!idleCallback) window.clearTimeout(handle); };
  }, []);
  const entries = stats ? ORDER.flatMap((key) => {
    const value = stats.items[key];
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? [[key, value] as const] : [];
  }) : [];
  const visible = entries.length >= 2;
  useEffect(() => {
    if (!visible || !ref.current) return;
    if (typeof IntersectionObserver === "undefined") { setPlay(true); return; }
    const observer = new IntersectionObserver((list) => { if (list.some((item) => item.isIntersecting)) { setPlay(true); observer.disconnect(); } }, { threshold: 0.35 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible]);
  if (!visible) return null;
  const words = captions();
  const leads = introductions();
  return (
    <section ref={ref} className="home-stats" data-home-module="M1-STATS" aria-label={t("使用統計")}>
      <div className="home-stats-card">
        <Vine className="home-stats-vine left" />
        <Vine className="home-stats-vine right" />
        <svg className="home-stats-grass" viewBox="0 0 600 28" preserveAspectRatio="none" aria-hidden="true"><path d="M0 28V14q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2q12 -14 24 0q12 -8 24 2V28Z" /></svg>
        <ul className="home-stats-list" style={{ "--stat-count": entries.length } as React.CSSProperties}>
          {entries.map(([key, value]) => (
            <li key={key} className={`home-stat home-stat-${key}`}>
              <span className="home-stat-icon"><ForestIcon name={ICONS[key]} /></span>
              <span className="home-stat-intro">{leads[key]}</span>
              <strong><CountUp value={value} play={play} /></strong>
              <span className="home-stat-caption">{words[key]}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
